import React from "react";
import { Loader2 } from "lucide-react";
import { Button } from "./Button";

/**
 * A button that runs one queue action.
 *
 * Every accept on a research screen is the same shape — disabled while anything at all is in
 * flight, a spinner only on the one that was pressed. Shared rather than copied per page because
 * the "anything in flight" half is the concurrency guard: it is what stops two accepts racing on
 * the same record and losing one another's acceptance stamp, and a second copy of a guard is a
 * guard that can be fixed in one place and left broken in the other.
 */
export function ActionButton({
  busy,
  actionKey,
  onClick,
  children,
  variant = "outline",
  className,
  disabled = false,
  // Everything else reaches the button. Without this the component silently swallowed any prop it
  // did not name — including the `aria-label` an icon-only destructive button depends on, which
  // made that button nameless to a screen reader while the call site looked correct.
  ...rest
}) {
  return (
    <Button
      size="sm"
      variant={variant}
      className={className}
      disabled={Boolean(busy) || disabled}
      onClick={onClick}
      {...rest}
    >
      {busy === actionKey ? <Loader2 className="h-4 w-4 animate-spin" /> : children}
    </Button>
  );
}

/**
 * Why a section of a research card has nothing in it.
 *
 * "Nothing found", "we could not tell" and "there is genuinely nothing here" all render as the
 * same blank space otherwise, which is the least useful thing a review screen can say — the admin
 * then has to open the entity to find out whether anything is owed. Each page supplies the reason
 * by reading its run's own markers.
 */
export function AbsentSection({ icon: Icon, title, reason }) {
  return (
    <section className="space-y-1">
      <p className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Icon className="h-4 w-4" /> {title}
      </p>
      <p className="text-xs text-muted-foreground">{reason}</p>
    </section>
  );
}
